import React, { useState, useCallback } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { ScrollArea } from '../../components/ui/scroll-area';
import { useAuth } from '../../App';
import {
  Search,
  CheckCircle2,
  XCircle,
  ChevronDown,
  ChevronRight,
  Clock,
  MessageSquare,
  Bot,
  Send,
  Shield,
  Database,
  Loader2,
  Activity,
  Brain,
  ArrowRight,
  Zap,
  CreditCard,
  Layers,
  Lock,
  Unlock,
  FileCheck
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const V2_STAGE_CONFIG = {
  webhook_received:           { icon: MessageSquare, label: 'Webhook Received',         color: 'bg-blue-500' },
  message_batched:            { icon: Layers,        label: 'Message Batched',           color: 'bg-slate-500' },
  conversation_lock_acquired: { icon: Lock,          label: 'Conversation Lock Acquired', color: 'bg-indigo-500' },
  message_persisted:          { icon: Database,      label: 'Message Persisted',          color: 'bg-purple-500' },
  ai_decision_made:           { icon: Brain,         label: 'AI Decision Made',           color: 'bg-amber-500' },
  entities_normalized:        { icon: FileCheck,     label: 'Entities Normalized',        color: 'bg-cyan-500' },
  intake_state_updated:       { icon: Database,      label: 'Intake State Updated',       color: 'bg-teal-500' },
  action_executed:            { icon: Zap,           label: 'Action Executed',            color: 'bg-orange-500' },
  response_generated:         { icon: Send,          label: 'Response Generated',         color: 'bg-green-500' },
  response_not_generated:     { icon: XCircle,       label: 'No Response Generated',      color: 'bg-red-400' },
  billing_processed:          { icon: CreditCard,    label: 'Billing Processed',          color: 'bg-emerald-500' },
  conversation_lock_released: { icon: Unlock,        label: 'Lock Released',              color: 'bg-indigo-400' },
  pipeline_complete:          { icon: CheckCircle2,  label: 'Pipeline Complete',          color: 'bg-emerald-600' },
};

const StatusDot = ({ status }) => {
  const color = status === 'success' ? 'bg-green-400' : status === 'failed' ? 'bg-red-400' : 'bg-yellow-400';
  return <span className={`inline-block w-2 h-2 rounded-full ${color}`} />;
};

const CollapsibleSection = ({ title, icon: Icon, badge, defaultOpen = false, children, testId }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden" data-testid={testId}>
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 dark:bg-gray-800/60 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors text-left"
      >
        <div className="flex items-center gap-2">
          {Icon && <Icon className="w-4 h-4 text-gray-500 dark:text-gray-400" />}
          <span className="text-sm font-semibold text-gray-800 dark:text-gray-200">{title}</span>
          {badge}
        </div>
        {open ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
      </button>
      {open && <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700">{children}</div>}
    </div>
  );
};

const KVRow = ({ label, value, mono = false }) => (
  <div className="flex items-start gap-3 py-1.5">
    <span className="text-xs text-gray-500 dark:text-gray-400 min-w-[120px] flex-shrink-0">{label}</span>
    <span className={`text-xs text-gray-900 dark:text-gray-100 break-all ${mono ? 'font-mono' : ''}`}>
      {value === null || value === undefined ? <span className="text-gray-400 italic">null</span> : String(value)}
    </span>
  </div>
);

const JsonBlock = ({ data, label }) => (
  <div>
    {label && <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">{label}</div>}
    <pre className="text-xs bg-gray-900 text-gray-100 p-3 rounded-md overflow-x-auto font-mono leading-relaxed">
      {JSON.stringify(data, null, 2)}
    </pre>
  </div>
);

const PipelineDebuggerPage = () => {
  const { user } = useAuth();
  const [searchType, setSearchType] = useState('message_id');
  const [searchValue, setSearchValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [traceData, setTraceData] = useState(null);
  const [traceList, setTraceList] = useState(null);

  const fetchTrace = useCallback(async () => {
    if (!searchValue.trim()) { setError('Please enter a search value'); return; }
    setLoading(true);
    setError(null);
    setTraceData(null);
    setTraceList(null);

    try {
      const token = localStorage.getItem('threados_token');
      let url;
      if (searchType === 'message_id') {
        url = `${API_URL}/api/admin/v2-pipeline-debug/message/${searchValue.trim()}`;
      } else {
        url = `${API_URL}/api/admin/v2-pipeline-debug/conversation/${searchValue.trim()}`;
      }

      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.detail || `HTTP ${response.status}`);
      }

      const data = await response.json();
      if (searchType === 'message_id') {
        setTraceData(data);
      } else {
        setTraceList(data.traces || []);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [searchType, searchValue]);

  const handleKeyPress = (e) => { if (e.key === 'Enter') fetchTrace(); };

  if (!user?.is_super_admin) {
    return (
      <div className="flex items-center justify-center h-full">
        <Card className="max-w-md">
          <CardContent className="pt-6">
            <div className="text-center">
              <Shield className="w-12 h-12 mx-auto text-red-500 mb-4" />
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Access Denied</h2>
              <p className="text-gray-600 dark:text-gray-400">This page is only accessible to Super Admins.</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="pipeline-debugger-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
          <Activity className="w-7 h-7 text-indigo-600" />
          V2 Pipeline Trace
        </h1>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
          Trace message processing through the V2 orchestration pipeline
        </p>
      </div>

      {/* Search */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Search Pipeline</CardTitle>
          <CardDescription>Enter a message ID for single trace, or conversation ID for all traces</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row gap-4">
            <select
              value={searchType}
              onChange={(e) => setSearchType(e.target.value)}
              className="h-10 px-3 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 flex-shrink-0"
              data-testid="search-type-select"
            >
              <option value="message_id">Message ID</option>
              <option value="conversation_id">Conversation ID</option>
            </select>
            <div className="flex-1">
              <Input
                placeholder={`Enter ${searchType.replace('_', ' ')}...`}
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                onKeyPress={handleKeyPress}
                data-testid="search-input"
              />
            </div>
            <Button onClick={fetchTrace} disabled={loading} data-testid="run-debug-btn">
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
              Run Debug
            </Button>
          </div>
          {error && (
            <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
              <p className="text-sm text-red-700 dark:text-red-400" data-testid="debug-error">{error}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Conversation trace list (when searching by conversation_id) */}
      {traceList && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Conversation Traces</CardTitle>
            <CardDescription>{traceList.length} message traces found</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {traceList.map((t, idx) => (
                <div
                  key={t.id || idx}
                  className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  onClick={() => { setSearchType('message_id'); setSearchValue(t.message_id); setTraceList(null); setTraceData(t); }}
                  data-testid={`trace-list-item-${idx}`}
                >
                  <div className="flex justify-between items-center">
                    <span className="font-mono text-sm text-gray-800 dark:text-gray-200">{t.message_id}</span>
                    <span className="text-xs text-gray-500">{t.timestamp ? new Date(t.timestamp).toLocaleString() : 'N/A'}</span>
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    {t.ai_decision?.action || 'unknown'} &middot; {t.stages?.length || 0} stages
                    {t.billing?.credits_deducted && <span className="text-emerald-500 ml-2">billed</span>}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Single message V2 trace */}
      {traceData && <TraceDetail trace={traceData} />}
    </div>
  );
};

const TraceDetail = ({ trace }) => {
  const stages = trace.stages || [];
  const ai = trace.ai_decision;
  const norm = trace.normalization;
  const sd = trace.state_diff;
  const ic = trace.intake_control;
  const exec = trace.execution;
  const billing = trace.billing;

  return (
    <Card data-testid="v2-trace-detail">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg flex items-center gap-2">
              V2 Pipeline Trace
              <Badge className="bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300 text-xs">V2</Badge>
            </CardTitle>
            <CardDescription>{stages.length} stages &middot; {trace.timestamp ? new Date(trace.timestamp).toLocaleString() : ''}</CardDescription>
          </div>
          {exec?.response_generated && <Badge className="bg-green-500 text-white">Response Generated</Badge>}
        </div>
      </CardHeader>
      <CardContent>
        {/* 1. MESSAGE INFO */}
        <div className="mb-5 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg grid grid-cols-1 md:grid-cols-3 gap-4 text-sm" data-testid="trace-message-info">
          <div>
            <span className="text-gray-500 dark:text-gray-400 text-xs">Message ID</span>
            <div className="font-mono text-gray-900 dark:text-white truncate text-xs">{trace.message_id}</div>
          </div>
          <div>
            <span className="text-gray-500 dark:text-gray-400 text-xs">Conversation ID</span>
            <div className="font-mono text-gray-900 dark:text-white truncate text-xs">{trace.conversation_id}</div>
          </div>
          <div>
            <span className="text-gray-500 dark:text-gray-400 text-xs">Timestamp</span>
            <div className="text-gray-900 dark:text-white text-xs">{trace.timestamp ? new Date(trace.timestamp).toLocaleString() : 'N/A'}</div>
          </div>
        </div>

        <ScrollArea className="h-[700px] pr-2">
          <div className="space-y-3 pb-4">

            {/* 2. PIPELINE STAGES */}
            <CollapsibleSection
              title="Pipeline Stages"
              icon={Layers}
              badge={<Badge variant="outline" className="text-xs ml-2">{stages.length}</Badge>}
              defaultOpen
              testId="section-pipeline-stages"
            >
              <div className="space-y-1.5">
                {stages.map((s, i) => {
                  const cfg = V2_STAGE_CONFIG[s.stage] || { icon: Activity, label: s.stage, color: 'bg-gray-500' };
                  const Icon = cfg.icon;
                  return (
                    <div key={i} className="flex items-center gap-3 py-1.5">
                      <div className={`${cfg.color} p-1 rounded text-white flex-shrink-0`}>
                        <Icon className="w-3 h-3" />
                      </div>
                      <StatusDot status={s.status} />
                      <span className="text-xs font-medium text-gray-800 dark:text-gray-200 flex-1">{cfg.label}</span>
                      {s.metadata && Object.keys(s.metadata).length > 0 && (
                        <span className="text-[10px] font-mono text-gray-500 dark:text-gray-400">
                          {Object.entries(s.metadata).map(([k,v]) => `${k}=${typeof v === 'object' ? JSON.stringify(v) : v}`).join(' ')}
                        </span>
                      )}
                      <span className="text-[10px] text-gray-400 flex-shrink-0">
                        {s.timestamp ? new Date(s.timestamp).toLocaleTimeString() : ''}
                      </span>
                    </div>
                  );
                })}
              </div>
            </CollapsibleSection>

            {/* 3. AI DECISION */}
            {ai && (
              <CollapsibleSection
                title="AI Decision"
                icon={Brain}
                badge={
                  <Badge className={`text-xs ml-2 ${ai.action === 'start_intake' ? 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/40 dark:text-cyan-300' : ai.action === 'continue_intake' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300' : ai.action === 'escalate' ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300' : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300'}`}>
                    {ai.action}
                  </Badge>
                }
                defaultOpen
                testId="section-ai-decision"
              >
                <div className="space-y-1">
                  <KVRow label="Intent" value={ai.intent} />
                  <KVRow label="Action" value={ai.action} />
                  <KVRow label="Confidence" value={ai.confidence != null ? `${(ai.confidence * 100).toFixed(0)}%` : null} />
                  <KVRow label="Next Slot" value={ai.next_slot} mono />
                  {ai.overridden && <KVRow label="Overridden" value={`Yes (original: ${ai.original_action})`} />}
                </div>
                {ai.entities && Object.keys(ai.entities).length > 0 && (
                  <div className="mt-3">
                    <JsonBlock data={ai.entities} label="Entities" />
                  </div>
                )}
                {ai.message_preview && (
                  <div className="mt-3 p-2 bg-gray-100 dark:bg-gray-800 rounded text-xs text-gray-700 dark:text-gray-300 italic">
                    "{ai.message_preview}"
                  </div>
                )}
              </CollapsibleSection>
            )}

            {/* 4. ENTITY NORMALIZATION */}
            {norm && (
              <CollapsibleSection
                title="Entity Normalization"
                icon={FileCheck}
                badge={norm.changed ?
                  <Badge className="bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300 text-xs ml-2">changed</Badge> :
                  <Badge variant="outline" className="text-xs ml-2">no change</Badge>
                }
                defaultOpen={norm.changed}
                testId="section-normalization"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <JsonBlock data={norm.before} label="Before" />
                  <JsonBlock data={norm.after} label="After" />
                </div>
              </CollapsibleSection>
            )}

            {/* 5. STATE DIFF */}
            {sd && (
              <CollapsibleSection
                title="State Diff (intake_data)"
                icon={Database}
                badge={
                  sd.fields_updated?.length > 0 ?
                    <Badge className="bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300 text-xs ml-2">
                      {sd.fields_updated.length} field{sd.fields_updated.length > 1 ? 's' : ''} updated
                    </Badge> :
                    <Badge variant="outline" className="text-xs ml-2">no change</Badge>
                }
                defaultOpen={sd.fields_updated?.length > 0}
                testId="section-state-diff"
              >
                {sd.fields_updated?.length > 0 && (
                  <div className="mb-3 space-y-1.5">
                    {sd.fields_updated.map((f, i) => (
                      <div key={i} className="flex items-center gap-2 text-xs">
                        <span className="font-mono font-medium text-gray-800 dark:text-gray-200 min-w-[100px]">{f.field}</span>
                        <span className="text-red-500 line-through">{f.old === null ? 'null' : JSON.stringify(f.old)}</span>
                        <ArrowRight className="w-3 h-3 text-gray-400 flex-shrink-0" />
                        <span className="text-green-600 dark:text-green-400 font-medium">{JSON.stringify(f.new)}</span>
                      </div>
                    ))}
                  </div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <JsonBlock data={sd.before} label="Before Step 7.5" />
                  <JsonBlock data={sd.after} label="After Step 7.5" />
                </div>
              </CollapsibleSection>
            )}

            {/* 6. INTAKE CONTROL */}
            {ic && (
              <CollapsibleSection title="Intake Control" icon={Lock} testId="section-intake-control">
                <div className="flex gap-6">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500">intake_active:</span>
                    <Badge className={ic.intake_active ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300' : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'}>
                      {String(ic.intake_active)}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500">intake_status:</span>
                    <Badge className={ic.intake_status === 'paused' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300' : 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300'}>
                      {ic.intake_status || 'N/A'}
                    </Badge>
                  </div>
                </div>
              </CollapsibleSection>
            )}

            {/* 7. EXECUTION */}
            {exec && (
              <CollapsibleSection title="Execution" icon={Zap} testId="section-execution">
                <div className="flex gap-6">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500">response_generated:</span>
                    {exec.response_generated ?
                      <CheckCircle2 className="w-4 h-4 text-green-500" /> :
                      <XCircle className="w-4 h-4 text-red-400" />}
                    <span className="text-xs">{String(exec.response_generated)}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500">fallback_triggered:</span>
                    <span className="text-xs">{String(exec.fallback_triggered)}</span>
                  </div>
                </div>
                {exec.error && (
                  <div className="mt-2 p-2 bg-red-50 dark:bg-red-900/20 rounded text-xs text-red-600">{exec.error}</div>
                )}
              </CollapsibleSection>
            )}

            {/* 8. BILLING */}
            {billing && (
              <CollapsibleSection title="Billing" icon={CreditCard} testId="section-billing">
                <div className="flex gap-6">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500">credits_deducted:</span>
                    <Badge className={billing.credits_deducted ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300' : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'}>
                      {String(billing.credits_deducted)}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500">ai_reply:</span>
                    <span className="text-xs">{String(billing.ai_reply)}</span>
                  </div>
                </div>
                {billing.skip_reason && (
                  <div className="mt-2 text-xs text-gray-500 italic">Skip reason: {billing.skip_reason}</div>
                )}
              </CollapsibleSection>
            )}

          </div>
        </ScrollArea>
      </CardContent>
    </Card>
  );
};

export default PipelineDebuggerPage;
